import React, { useEffect, useState } from "react";
import { FaPlus } from "react-icons/fa";
import UsersService from "../../../services/v2/management/users-service";
import UserAdd from "./UserAdd";
import UserDelete from "./UserDelete";
import UserUpdate from "./UserUpdate";
import PermissionsKo from "../../shared/PermissionsKo";

const UsersTable = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUnauthorized, setIsUnauthorized] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await UsersService.list({});
      setUsers(response.data.data || []);
    } catch (error) {
      if (error.response && error.response.status === 403) {
        setIsUnauthorized(true);
      }
      setError("Failed to load users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const closeModals = () => {
    setSelectedUser(null);
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
  };
  if (isUnauthorized) {
    return <PermissionsKo />;
  }

  const downloadUsersCSV = () => {
    const headers = ["Name", "Email", "Permission Group"];

    const rows = users.map((user) => [
      `${user.name || ""} ${user.surname1 || ""}`.trim(),
      user.email || "",
      user.permissionGroup?.name || "Default",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((value) => `"${String(value).replace(/"/g, '""')}"`)
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "users_permissions.csv";
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="flex justify-between items-center mt-4">
        <div className="flex items-center gap-2">
          <button
            className="flex items-center gap-2 px-2 py-1 bg-[#2B6CB0] text-white rounded-lg text-sm"
            onClick={() => setShowAddModal(true)}
          >
            <FaPlus />
            Add User
          </button>

          <button
            className="flex items-center gap-2 px-2 py-1 bg-gray-600 text-white rounded-lg text-sm disabled:opacity-50"
            onClick={downloadUsersCSV}
            disabled={loading || users.length === 0}
          >
            Download CSV
          </button>
        </div>

        <UserAdd
          isOpen={showAddModal}
          onClose={closeModals}
          onSuccess={fetchUsers}
        />

        <UserUpdate
          isOpen={showEditModal}
          onClose={closeModals}
          onSuccess={fetchUsers}
          user={selectedUser}
        />

        {showDeleteModal && (
          <UserDelete
            user={selectedUser}
            close={closeModals}
            refresh={fetchUsers}
          />
        )}
      </div>
      <div className="max-h-full overflow-auto bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 shadow-md">
        <table className="w-full">
          <thead
            className="sticky top-0 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide rounded-t-lg"
            style={{ height: "3vh" }}
          >
            <tr>
              {["Name", "Surname", "Email", "Permission Group", "Actions"].map(
                (header) => (
                  <th
                    key={header}
                    className="px-2 py-2 border-b border-gray-300 text-left font-semibold"
                  >
                    {header}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center py-10">
                  <div className="flex flex-col items-center text-gray-500 dark:text-gray-400">
                    <svg
                      className="animate-spin h-8 w-8 text-gray-500 dark:text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z"
                      ></path>
                    </svg>
                    <p className="mt-3 text-lg">Loading data, please wait...</p>
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="5" className="text-center py-10">
                  <div className="flex flex-col items-center text-red-600">
                    <p className="mt-3 text-lg font-semibold">
                      Something went wrong!
                    </p>
                    <p className="text-sm">
                      Please reload the page to try again.
                    </p>
                  </div>
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-10">
                  <div className="flex flex-col items-center text-gray-500 dark:text-gray-400">
                    <p className="mt-3 text-lg">No data to display.</p>
                  </div>
                </td>
              </tr>
            ) : (
              users.map((user, index) => (
                <tr
                  key={user._id}
                  className={`${
                    index % 2 === 0
                      ? "bg-white dark:bg-gray-800"
                      : "bg-gray-50 dark:bg-primary-dark"
                  } hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200`}
                >
                  <td className="px-2 py-2 text-gray-700 dark:text-gray-300 text-sm">
                    {user.name}
                  </td>
                  <td className="px-2 py-2 text-gray-700 dark:text-gray-300 text-sm">
                    {user.surname1}
                  </td>
                  <td className="px-2 py-2 text-gray-700 dark:text-gray-300 text-sm">
                    {user.email}
                  </td>
                  <td className="px-2 py-2 text-gray-700 dark:text-gray-300 text-sm">
                    {user.permissionGroup?.name || "Default"}
                  </td>
                  <td className="px-2 py-2 text-gray-700 dark:text-gray-300 text-sm">
                    <div className="flex gap-2">
                      <button
                        className="w-6 h-6 flex items-center justify-center bg-gray-200 dark:bg-gray-600 rounded-full"
                        title="Edit"
                        onClick={() => handleEditClick(user)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="h-5 w-5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                          />
                        </svg>
                      </button>
                      <button
                        className="w-6 h-6 flex items-center justify-center bg-gray-200 dark:bg-gray-600  rounded-full"
                        title="Delete"
                        onClick={() => handleDeleteClick(user)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="h-5 w-5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default UsersTable;
